import { ViewportScroller } from '@angular/common';
import { Event, NavigationEnd, NavigationStart, Router, Scroll } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, switchMap, withLatestFrom } from 'rxjs/operators';

import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { DateAdapter } from '@angular/material/core';
import { HighchartsChartModule } from 'highcharts-angular';
import { DragScrollComponent, DragScrollItemDirective } from 'ngx-drag-scroll';

import { CustomDateAdapter } from '@services';

import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgxMaskModule } from 'ngx-mask';
import { FeaturesModule } from '../features/features.module';
import { ExecutablePipe } from '../operational-data-common/feature-pipes/executable.pipe';
import { UnitPipe } from '../operational-data-common/feature-pipes/unit.pipe';
import { SharedDependenciesModule } from '../shared';
import { SharedUiModule } from '../shared-ui';
import { InputValuePipe } from '../shared/pipes/input-value/input-value.pipe';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GenericInfoBannerComponent } from '../+dashboard/dashboard-view/dashboard-banners/generic-info-banner/generic-info-banner.component';
import { OptimizationBannerComponent } from '../+dashboard/dashboard-view/dashboard-banners/optimization-banner/optimization-banner.component';
import { FeatureLikePipe } from '../operational-data-common/feature-pipes/feature-like.pipe';
import { Value2Pipe } from '../operational-data-common/feature-pipes/value-strategy-based/value2.pipe';
import { DiverterValvePipe } from '../operational-data-common/translation-pipes/diverter-valve.pipe';
import { TranslateGatewayConnectionModePipe } from '../operational-data-common/translation-pipes/translate-gateway-connection-mode.pipe';
import { ViDswFooterComponent } from '../shared/+design-system-web/vi-dsw-footer/vi-dsw-footer.component';
import { DiagnosticsOverlayModule } from './diagnostics-overlay/diagnostics-overlay.module';
import { DiagnosticsModule } from './diagnostics-overlay/tabs-content/extended-parameters/diagnostics.module';
import { InstallationLimitedComponent } from './installation-limited/installation-limited.component';
import { BatteryOverallComponent } from './installation/battery-overall/battery-overall.component';
import { BatteryOverlayComponent } from './installation/battery/battery-overlay/battery-overlay.component';
import { BatteryComponent } from './installation/battery/battery.component';
import { BufferTankComponent } from './installation/buffer-tank/buffer-tank.component';
import { LagDeviceDetailsOverlayComponent } from './installation/cascades/lag-device-details-overlay/lag-device-details-overlay.component';
import { LagDeviceTileComponent } from './installation/cascades/lag-device-tile/lag-device-tile.component';
import { SequencingStrategyTileComponent } from './installation/cascades/sequencing-strategy-tile/sequencing-strategy-tile.component';
import { SequencingStrategyComponent } from './installation/cascades/sequencing-strategy/sequencing-strategy.component';
import { CircuitOperatingModePanelComponent } from './installation/circuit-operating-mode-panel/circuit-operating-mode-panel.component';
import { ConsumptionHistoryComponent } from './installation/consumption-history/consumption-history.component';
import { DragDropMobileComponent } from './installation/drag-drop-layout/drag-drop-mobile.component';
import { EcoWaterOverlayComponent } from './installation/eco-water/eco-water-overlay/eco-water-overlay.component';
import { EcoWaterComponent } from './installation/eco-water/eco-water.component';
import { ElectricHeaterOverlayComponent } from './installation/electric-heater/electric-heater-overlay/electric-heater-overlay.component';
import { ElectricHeaterComponent } from './installation/electric-heater/electric-heater.component';
import { EnergyCockpitPanelComponent } from './installation/energy-cockpit-panel/energy-cockpit-panel.component';
import { CalculationStatusPipe } from './installation/energy-flow/calculation-status.pipe';
import { EnergyFlowComponent } from './installation/energy-flow/energy-flow.component';
import { ChartParametersPopupComponent } from './installation/energy-historical-data-modal/chart-parameters-popup/chart-parameters-popup.component';
import { DayDatepickerComponent } from './installation/energy-historical-data-modal/day-datepicker/day-datepicker.component';
import { EnergyFiltersHeaderComponent } from './installation/energy-historical-data-modal/energy-filters-header/energy-filters-header.component';
import { EnergyHistoryChartLegendComponent } from './installation/energy-historical-data-modal/energy-history-chart-legend/energy-history-chart-legend.component';
import { EnergyHistoryChartComponent } from './installation/energy-historical-data-modal/energy-history-chart/energy-history-chart.component';
import { YearDatepickerComponent } from './installation/energy-historical-data-modal/year-datepicker/year-datepicker.component';
import { DefaultFeatureTilesComponent } from './installation/feature-tiles/default-feature-tiles/default-feature-tiles.component';
import { V2CascadeFeatureTilesComponent } from './installation/feature-tiles/v2-cascade-feature-tiles/v2-cascade-feature-tiles.component';
import { V2DeviceFeatureTilesComponent } from './installation/feature-tiles/v2-device-feature-tiles/v2-device-feature-tiles.component';
import { WallboxFeatureTilesComponent } from './installation/feature-tiles/wallbox-feature-tiles/wallbox-feature-tiles.component';
import { FirmwareUpdateModalComponent } from './installation/firmware-update-modal/firmware-update-modal.component';
import { FirmwareUpdateOneTimeApprovalComponent } from './installation/firmware-update-modal/firmware-update-one-time-approval/firmware-update-one-time-approval.component';
import { FirmwareUpdateProgressComponent } from './installation/firmware-update-modal/firmware-update-progress/firmware-update-progress.component';
import { FirmwareUpdateStartComponent } from './installation/firmware-update-modal/firmware-update-start/firmware-update-start.component';
import { FirmwareUpdateUpToDateComponent } from './installation/firmware-update-modal/firmware-update-up-to-date/firmware-update-up-to-date.component';
import { FirmwareUpdateVerificationComponent } from './installation/firmware-update-modal/firmware-update-verification/firmware-update-verification.component';
import { FirmwareUpdateOperationStatusComponent } from './installation/firmware-update-modal/firmware-update-version-list/firmware-update-operation-status/firmware-update-operation-status.component';
import { FirmwareUpdateVersionListBannerComponent } from './installation/firmware-update-modal/firmware-update-version-list/firmware-update-version-list-banner/firmware-update-version-list-banner.component';
import { FirmwareUpdateVersionListCheckComponent } from './installation/firmware-update-modal/firmware-update-version-list/firmware-update-version-list-check/firmware-update-version-list-check.component';
import { FirmwareUpdateVersionListFinalizeComponent } from './installation/firmware-update-modal/firmware-update-version-list/firmware-update-version-list-finalize/firmware-update-version-list-finalize.component';
import { FirmwareUpdateVersionListUpdatingComponent } from './installation/firmware-update-modal/firmware-update-version-list/firmware-update-version-list-updating/firmware-update-version-list-updating.component';
import { FirmwareUpdateVersionListComponent } from './installation/firmware-update-modal/firmware-update-version-list/firmware-update-version-list.component';
import { FuelCellDetailsOverlayComponent } from './installation/fuel-cell/fuel-cell-overlay/fuel-cell-overlay.component';
import { FuelCellComponent } from './installation/fuel-cell/fuel-cell.component';
import { DeregisterConfirmationModalComponent } from './installation/gateway-features-panel/deregister-confirmation-modal/deregister-confirmation-modal.component';
import { AccessListTableComponent } from './installation/gateway-features-panel/gateway-details-overlay/access-list/access-list-table/access-list-table.component';
import { AccessListUserComponent } from './installation/gateway-features-panel/gateway-details-overlay/access-list/access-list-user/access-list-user.component';
import { AccessListComponent } from './installation/gateway-features-panel/gateway-details-overlay/access-list/access-list.component';
import { GatewayDetailsOverlayComponent } from './installation/gateway-features-panel/gateway-details-overlay/gateway-details-overlay.component';
import { InvitationListComponent } from './installation/gateway-features-panel/gateway-details-overlay/invitation-list/invitation-list.component';
import { GatewayFeaturesPanelComponent } from './installation/gateway-features-panel/gateway-features-panel.component';
import { GatewayFirmwareUpdateButtonComponent } from './installation/gateway-features-panel/gateway-firmware-update-button/gateway-firmware-update-button.component';
import { GatewayMenuComponent } from './installation/gateway-features-panel/gateway-menu/gateway-menu.component';
import { ReplaceGatewayModalComponent } from './installation/gateway-features-panel/replace-gateway-modal/replace-gateway-modal.component';
import { GenericConsumptionChartOverlayComponent } from './installation/generic-consumption-chart-overlay/generic-consumption-chart-overlay.component';
import { GenericConsumptionChartPanelComponent } from './installation/generic-consumption-chart-panel/generic-consumption-chart-panel.component';
import { GenericConsumptionOverlayComponent } from './installation/generic-consumption-overlay/generic-consumption-overlay.component';
import { GenericConsumptionPanelComponent } from './installation/generic-consumption-panel/generic-consumption-panel.component';
import { EditHeatingCircuitNameModalComponent } from './installation/heating-circuit-features-panel/edit-heating-circuit-name-modal/edit-heating-circuit-name-modal.component';
import { HeatCurvePanelComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/heat-curve-panel/heat-curve-panel.component';
import { HeatingCircuitDetailsOverlayComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/heating-circuit-details-overlay.component';
import { ComfortModePanelComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/comfort-mode-panel/comfort-mode-panel.component';
import { CoolingTemperatureComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/cooling-temperature/cooling-temperature.component';
import { EcoModePanelComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/eco-mode-panel/eco-mode-panel.component';
import { FlowLimitTempConfirmationDialogComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/flow-limit-temp-confirmation-dialog/flow-limit-temp-confirmation-dialog.component';
import { FlowLimitTemperaturePanelComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/flow-limit-temperature-panel/flow-limit-temperature-panel.component';
import { HeatingPanelComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/heating-panel.component';
import { HeatingTemperatureComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/heating-temperature/heating-temperature.component';
import { MfhProgramsPanelComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/multi-family-house-panel/mfh-programs-panel/mfh-programs-panel.component';
import { MfhTemperaturePanelComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/multi-family-house-panel/mfh-temperature-panel/mfh-temperature-panel.component';
import { MultiFamilyHousePanelComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/multi-family-house-panel/multi-family-house-panel.component';
import { ProgramTogglePanelComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/program-toggle-panel/program-toggle-panel.component';
import { SingleFamilyHousePanelComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/heating-panel/single-family-house-panel/single-family-house-panel.component';
import { TemperatureScheduleManagerComponent } from './installation/heating-circuit-features-panel/heating-circuit-details-overlay/temperature-schedule-manager/temperature-schedule-manager.component';
import { HeatingCircuitFeaturesPanelComponent } from './installation/heating-circuit-features-panel/heating-circuit-features-panel.component';
import { HeatingCircuitIgnoredModalComponent } from './installation/heating-circuit-ignored-modal/heating-circuit-ignored-modal.component';
import { HeatingZoneHeatCurvePanelComponent } from './installation/heating-zone-heat-curve-manager/heating-zone-heat-curve-panel/heating-zone-heat-curve-panel.component';
import { CallForHeatHeatingZoneDetailsOverlayComponent } from './installation/heating-zone/call-for-heat-heating-zone/call-for-heat-heating-zone-details-overlay/call-for-heat-heating-zone-details-overlay.component';
import { CallForHeatHeatingZoneFeaturesPanelComponent } from './installation/heating-zone/call-for-heat-heating-zone/call-for-heat-heating-zone-features-panel.component';
import { CircuitHeatingZoneDetailsOverlayComponent } from './installation/heating-zone/circuit-heating-zone/circuit-heating-zone-details-overlay/circuit-heating-zone-details-overlay.component';
import { CircuitHeatingZoneFeaturesPanelComponent } from './installation/heating-zone/circuit-heating-zone/circuit-heating-zone-features-panel.component';
import { HemsActivateDeactivateSettingOptionComponent } from './installation/hems/hems-settings/hems-activate-deactivate-setting-option/hems-activate-deactivate-setting-option.component';
import { HemsFinancialOptimizationComponent } from './installation/hems/hems-settings/hems-financial-optimization/hems-financial-optimization.component';
import { HemsSettingsComponent } from './installation/hems/hems-settings/hems-settings.component';
import { HemsStepperSettingOptionComponent } from './installation/hems/hems-settings/hems-stepper-setting-option/hems-stepper-setting-option.component';
import { HemsComponent } from './installation/hems/hems.component';
import { HolidayDatepickerModalComponent } from './installation/holiday-mode-panel/holiday-datepicker-modal/holiday-datepicker-modal.component';
import { HolidayModeDatepickerComponent } from './installation/holiday-mode-panel/holiday-mode-datepicker/holiday-mode-datepicker.component';
import { HolidayModePanelComponent } from './installation/holiday-mode-panel/holiday-mode-panel.component';
import { HolidayModeSelectComponent } from './installation/holiday-mode-panel/holiday-mode-select/holiday-mode-select.component';
import { CascadeDevicesBadgePipe } from './installation/installation-details-header/cascade-count-badge-pipe/cascade-devices-badge.pipe';
import { CommonInstallationTileComponent } from './installation/installation-details-header/common-installation-tile/common-installation-tile.component';
import { DeviceTileWrapperComponent } from './installation/installation-details-header/device-tile-wrapper/device-tile-wrapper.component';
import { InstallationDetailsHeaderComponent } from './installation/installation-details-header/installation-details-header.component';
import { ControlUnitsOverlayComponent } from './installation/installation-details-menu-bar/control-units-overlay/control-units-overlay.component';
import { ControlUnitsComponent } from './installation/installation-details-menu-bar/control-units-overlay/control-units/control-units.component';
import { CustomerIdentifierComponent } from './installation/installation-details-menu-bar/event-log/event-list/customer-identifier/customer-identifier.component';
import { EventItemComponent } from './installation/installation-details-menu-bar/event-log/event-list/event-item/event-item.component';
import { EventListComponent } from './installation/installation-details-menu-bar/event-log/event-list/event-list.component';
import { EventLogComponent } from './installation/installation-details-menu-bar/event-log/event-log.component';
import { InstallationDetailsMenuBarComponent } from './installation/installation-details-menu-bar/installation-details-menu-bar.component';
import { InstallationOrderedElementComponent } from './installation/installation-ordered-element/installation-ordered-element.component';
import { InstallationComponent } from './installation/installation.component';
import { InternalHydraulicDetailsOverlayComponent } from './installation/internal-hydraulic-features-panel/internal-hydraulic-details-overlay/internal-hydraulic-details-overlay.component';
import { InternalHydraulicPermanentInspectionTeaserComponent } from './installation/internal-hydraulic-features-panel/internal-hydraulic-details-overlay/internal-hydraulic-permanent-inspection-teaser/internal-hydraulic-permanent-inspection-teaser.component';
import { InternalHydraulicRelatedParametersComponent } from './installation/internal-hydraulic-features-panel/internal-hydraulic-details-overlay/internal-hydraulic-related-parameters/internal-hydraulic-related-parameters.component';
import { InternalHydraulicVolumeFlowExplanationComponent } from './installation/internal-hydraulic-features-panel/internal-hydraulic-details-overlay/internal-hydraulic-volume-flow-explanation/internal-hydraulic-volume-flow-explanation.component';
import { InternalHydraulicFeaturesPanelComponent } from './installation/internal-hydraulic-features-panel/internal-hydraulic-features-panel.component';
import { LectureCodeAddressPanelComponent } from './installation/lecture-code-address-panel/lecture-code-address-panel.component';
import { MaintenanceChecklistComponent } from './installation/maintenance-checklist/maintenance-checklist.component';
import { MaintenanceInformationOverlayComponent } from './installation/maintenance-information-overlay/maintenance-information-overlay.component';
import { MissedHcAssignmentWarningModalComponent } from './installation/missed-hc-assignment-warning-modal/missed-hc-assignment-warning-modal.component';
import { NbiotComponent } from './installation/nbiot/nbiot.component';
import { OverlayComponent } from './installation/overlay/overlay.component';
import { PartnerAccessOverlayComponent } from './installation/partner-access-overlay/partner-access-overlay.component';
import { PartnerAccessPillsPipe } from './installation/partner-access-overlay/partner-access-settings-overlay/partner-access-settings-item/partner-access-pills.pipe';
import { PartnerAccessSettingsItemComponent } from './installation/partner-access-overlay/partner-access-settings-overlay/partner-access-settings-item/partner-access-settings-item.component';
import { PartnerAccessSettingsOverlayComponent } from './installation/partner-access-overlay/partner-access-settings-overlay/partner-access-settings-overlay.component';
import { PartnerAccessShareDeviceModalComponent } from './installation/partner-access-overlay/partner-access-share-device-modal/partner-access-share-device-modal.component';
import { PartnerAccessSharePillPipePipe } from './installation/partner-access-overlay/partner-access-share-device-modal/partner-access-share-pill-pipe.pipe';
import { PhotovoltaicOverallComponent } from './installation/photovoltaic-overall/photovoltaic-overall.component';
import { PhotovoltaicsOverlayComponent } from './installation/photovoltaics/photovoltaics-overlay/photovoltaics-overlay.component';
import { PhotovoltaicsComponent } from './installation/photovoltaics/photovoltaics.component';
import { PowerLimitationHistoryFiltersComponent } from './installation/power-limitation-history-overlay/power-limitation-history-filters/power-limitation-history-filters.component';
import { PowerLimitationHistoryListComponent } from './installation/power-limitation-history-overlay/power-limitation-history-list/power-limitation-history-list.component';
import { PowerLimitationHistoryOverlayComponent } from './installation/power-limitation-history-overlay/power-limitation-history-overlay.component';
import { PvHeaterOverlayComponent } from './installation/pv-heater/pv-heater-overlay/pv-heater-overlay/pv-heater-overlay.component';
import { PvHeaterComponent } from './installation/pv-heater/pv-heater.component';
import { DefrostingProcessRunningComponent } from './installation/refrigerant-circuit-features-panel/refrigerant-circuit-details-overlay/defrosting-process-running/defrosting-process-running.component';
import { DefrostingRemoteTriggerComponent } from './installation/refrigerant-circuit-features-panel/refrigerant-circuit-details-overlay/defrosting-remote-trigger/defrosting-remote-trigger.component';
import { RefrigerantCircuitDetailsOverlayComponent } from './installation/refrigerant-circuit-features-panel/refrigerant-circuit-details-overlay/refrigerant-circuit-details-overlay.component';
import { RefrigerantCircuitFeaturesPanelComponent } from './installation/refrigerant-circuit-features-panel/refrigerant-circuit-features-panel.component';
import { RefrigerantCircuitHistoryDetailsComponent } from './installation/refrigerant-circuit-features-panel/refrigerant-circuit-history-modal/refrigerant-circuit-history-details/refrigerant-circuit-history-details.component';
import { RefrigerantCircuitHistoryItemComponent } from './installation/refrigerant-circuit-features-panel/refrigerant-circuit-history-modal/refrigerant-circuit-history-item/refrigerant-circuit-history-item.component';
import { RefrigerantCircuitHistoryModalComponent } from './installation/refrigerant-circuit-features-panel/refrigerant-circuit-history-modal/refrigerant-circuit-history-modal.component';
import { RemoteControllerOverlayComponent } from './installation/remote-controller/remote-controller-overlay/remote-controller-overlay.component';
import { RemoteControllerComponent } from './installation/remote-controller/remote-controller.component';
import { RemoteServiceChartComponent } from './installation/remote-service/remote-service-chart/remote-service-chart.component';
import { RemoteServiceOverlayComponent } from './installation/remote-service/remote-service-overlay/remote-service-overlay.component';
import { RemoteServiceTabsComponent } from './installation/remote-service/remote-service-tabs/remote-service-tabs.component';
import { RemoteServiceThresholdsTileComponent } from './installation/remote-service/remote-service-tresholds/remote-service-thresholds-tile/remote-service-thresholds-tile.component';
import { RemoteServiceThresholdsComponent } from './installation/remote-service/remote-service-tresholds/remote-service-thresholds.component';
import { RemoteServiceDiagnosisComponent } from './installation/remote-service/views/remote-service-diagnosis/remote-service-diagnosis.component';
import { RemoteServiceFeaturesComponent } from './installation/remote-service/views/remote-service-features/remote-service-features.component';
import { RemoteServiceReportComponent } from './installation/remote-service/views/remote-service-report/remote-service-report.component';
import { ServiceInstructionComponent } from './installation/service-instruction/service-instruction.component';
import { ServiceLayerOverlayComponent } from './installation/service-layer-overlay/service-layer-overlay.component';
import { SetHolidayModeComponent } from './installation/set-holiday-mode/set-holiday-mode.component';
import { SolarOverlayComponent } from './installation/solar/solar-overlay/solar-overlay.component';
import { SolarComponent } from './installation/solar/solar.component';
import { SolarLogOverlayComponent } from './installation/solarlog/solarlog-overlay/solarlog-overlay.component';
import { SolarLogComponent } from './installation/solarlog/solarlog.component';
import { ActiveErrorComponent } from './installation/sticky-panel/active-errors-card/active-error/active-error.component';
import { ActiveErrorsCardComponent } from './installation/sticky-panel/active-errors-card/active-errors-card.component';
import { FloorHeatingOverHeatingErrorComponent } from './installation/sticky-panel/active-errors-card/floor-heating-over-heating-error/floor-heating-over-heating-error.component';
import { LockedSystemErrorComponent } from './installation/sticky-panel/active-errors-card/locked-system-error/locked-system-error.component';
import { MaintenanceNotificationComponent } from './installation/sticky-panel/active-errors-card/maintenance-notification/maintenance-notification.component';
import { PowerLimitationStatusesComponent } from './installation/sticky-panel/active-errors-card/power-limitation-statuses/power-limitation-statuses.component';
import { BookedPackageCardComponent } from './installation/sticky-panel/booked-package-card/booked-package-card.component';
import { BuildingManagerCardComponent } from './installation/sticky-panel/building-manager-card/building-manager-card.component';
import { RoleManagementModalComponent } from './installation/sticky-panel/building-manager-card/role-management-modal/role-management-modal.component';
import { HolidayCardComponent } from './installation/sticky-panel/holiday-card/holiday-card.component';
import { HolidayIconComponent } from './installation/sticky-panel/holiday-icon/holiday-icon.component';
import { InstallationCardComponent } from './installation/sticky-panel/installation-card/installation-card.component';
import { NoteCardComponent } from './installation/sticky-panel/note-card/note-card.component';
import { NoteModalComponent } from './installation/sticky-panel/note-card/note-modal/note-modal.component';
import { EditOwnerCardDialogComponent } from './installation/sticky-panel/owner-card/edit-owner-card-dialog/edit-owner-card-dialog.component';
import { OwnerCardComponent } from './installation/sticky-panel/owner-card/owner-card.component';
import { TransferInstallationDialogComponent } from './installation/sticky-panel/owner-card/transfer-installation-dialog/transfer-installation-dialog.component';
import { OwnerNamePanelComponent } from './installation/sticky-panel/owner-name-panel/owner-name-panel.component';
import { RemoteServiceCardComponent } from './installation/sticky-panel/remote-service-card/remote-service-card.component';
import { StickyPanelComponent } from './installation/sticky-panel/sticky-panel.component';
import { SystemV2CascadeComponent } from './installation/system-v2-cascade/system-v2-cascade.component';
import { DepartmentsDropdownComponent } from './installation/system/system-overlay/departments-dropdown/departments-dropdown.component';
import { DeviceOverlayComponent } from './installation/system/system-overlay/device-overlay/device-overlay.component';
import { FanRingCondensatePanHeaterComponent } from './installation/system/system-overlay/device-overlay/fan-ring-condensate-pan-heater/fan-ring-condensate-pan-heater.component';
import { SmartHomeComponentsComponent } from './installation/system/system-overlay/device-overlay/smart-home-components/smart-home-components.component';
import { EcotronicDetailsComponent } from './installation/system/system-overlay/ecotronic-details/ecotronic-details.component';
import { RefrigerantLoadClassesDetailsComponent } from './installation/system/system-overlay/refrigerant-load-classes-details/refrigerant-load-classes-details.component';
import { SystemConfigurationsComponent } from './installation/system/system-overlay/system-configurations/system-configurations.component';
import { SystemDetailsComponent } from './installation/system/system-overlay/system-details/system-details.component';
import { SystemOverlayComponent } from './installation/system/system-overlay/system-overlay.component';
import { SystemServiceIntervalComponent } from './installation/system/system-overlay/system-service-interval/system-service-interval.component';
import { SystemComponent } from './installation/system/system.component';
import { TrendingChartLegendComponent } from './installation/trending/trending-chart/trending-chart-legend/trending-chart-legend.component';
import { TrendingLineChartComponent } from './installation/trending/trending-chart/trending-line-chart/trending-line-chart.component';
import { TrendingXrangeChartComponent } from './installation/trending/trending-chart/trending-xrange-chart/trending-xrange-chart.component';
import { TrendingDateRangeComponent } from './installation/trending/trending-date-range/trending-date-range.component';
import { TrendingComponent } from './installation/trending/trending.component';
import { SelectVitotrolDeviceModalComponent } from './installation/ventilation/select-vitotrol-device-modal/select-vitotrol-device-modal.component';
import { UnassignVitotrolRemoteControllerConfirmModalComponent } from './installation/ventilation/unassign-vitotrol-remote-controller-confirm-modal/unassign-vitotrol-remote-controller-confirm-modal.component';
import { VentilationAutomatedModeLevelComponent } from './installation/ventilation/ventilation-automated-mode-level/ventilation-automated-mode-level.component';
import { VentilationBypassComponent } from './installation/ventilation/ventilation-bypass/ventilation-bypass.component';
import { VentilationFrostProtectionComponent } from './installation/ventilation/ventilation-frost-protection/ventilation-frost-protection.component';
import { VentilationModePanelComponent } from './installation/ventilation/ventilation-mode-panel/ventilation-mode-panel.component';
import { VentilationDiagnosticInformationComponent } from './installation/ventilation/ventilation-overlay/ventilation-diagnostic-information/ventilation-diagnostic-information.component';
import { VentilationOverlayComponent } from './installation/ventilation/ventilation-overlay/ventilation-overlay.component';
import { VentilationPanelComponent } from './installation/ventilation/ventilation-overlay/ventilation-panel/ventilation-panel.component';
import { VentilationRemoteControllerReadingsComponent } from './installation/ventilation/ventilation-overlay/ventilation-remote-controller-readings/ventilation-remote-controller-readings.component';
import { VentilationSensorReadingsComponent } from './installation/ventilation/ventilation-overlay/ventilation-sensor-readings/ventilation-sensor-readings.component';
import { VentilationQuickSelectionsComponent } from './installation/ventilation/ventilation-quick-selections/ventilation-quick-selections.component';
import { VentilationResetToDefaultConfirmModalComponent } from './installation/ventilation/ventilation-reset-to-default-confirm-modal/ventilation-reset-to-default-confirm-modal.component';
import { VentilationComponent } from './installation/ventilation/ventilation.component';
import { VitotrolRemoteControllerComponent } from './installation/ventilation/vitotrol-remote-controller/vitotrol-remote-controller.component';
import { ViCarePlansOverlayComponent } from './installation/vicare-plans/vicare-plans-overlay/vicare-plans-overlay.component';
import { ViCarePlansComponent } from './installation/vicare-plans/vicare-plans.component';
import { RepeatersWifiLqiComponent } from './installation/vicare-thermostat/repeaters-wifi-lqi/repeaters-wifi-lqi.component';
import { VicareThermostatOverlayComponent } from './installation/vicare-thermostat/vicare-thermostat-overlay/vicare-thermostat-overlay.component';
import { VicareThermostatComponent } from './installation/vicare-thermostat/vicare-thermostat.component';
import { WallboxChargingModeOverlayComponent } from './installation/wallbox/wallbox-charging-mode-overlay/wallbox-charging-mode-overlay.component';
import { WallboxChargingModeComponent } from './installation/wallbox/wallbox-charging-mode/wallbox-charging-mode.component';
import { WallboxSystemOverlayComponent } from './installation/wallbox/wallbox-system-overlay/wallbox-system-overlay.component';
import { WallboxSystemComponent } from './installation/wallbox/wallbox-system/wallbox-system.component';
import { DomesticHotWaterPanelComponent } from './installation/warm-water/warm-water-overlay/domestic-hot-water-panel/domestic-hot-water-panel.component';
import { HygieneComponent } from './installation/warm-water/warm-water-overlay/hygiene/hygiene.component';
import { RecirculationSettingsComponent } from './installation/warm-water/warm-water-overlay/recirculation-settings/recirculation-settings.component';
import { WarmWaterOverlayComponent } from './installation/warm-water/warm-water-overlay/warm-water-overlay.component';
import { WarmWaterComponent } from './installation/warm-water/warm-water.component';
import { InstallationsRouting } from './installations.routing';
import { AnnouncementBannerComponent } from './installations/announcement-banner/announcement-banner.component';
import { InstallationDashboardComponent } from './installations/installation-dashboard/installation-dashboard.component';
import { InstallationFilterComponent } from './installations/installation-filter/installation-filter.component';
import { InstallationItemStatusComponent } from './installations/installation-list/installation-item-status/installation-item-status.component';
import { InstallationItemComponent } from './installations/installation-list/installation-item/installation-item.component';
import { InstallationListComponent } from './installations/installation-list/installation-list.component';
import { MainDevicesListLargeComponent } from './installations/installation-list/main-devices-list-large/main-devices-list-large.component';
import { MainDevicesListSmallComponent } from './installations/installation-list/main-devices-list-small/main-devices-list-small.component';
import { MainDeviceTileComponent } from './installations/installation-list/main-devices-tile/main-devices-tile.component';
import { OperatingModeIndicatorComponent } from './installations/installation-list/operating-mode-indicator/operating-mode-indicator.component';
import { OperatingModeIndicatorsComponent } from './installations/installation-list/operating-mode-indicators/operating-mode-indicators.component';
import { InstallationsComponent } from './installations/installations.component';
import { LimitedSearchComponent } from './installations/limited-search/limited-search.component';
import { OptimizationOverlayComponent } from './installations/optimization-overlay/optimization-overlay.component';
import { MapComponent } from './map/map.component';
import { SingleRoomControlModule } from './single-room-control/single-room-control.module';
import { BufferTankOverlayComponent } from './installation/buffer-tank/buffer-tank-overlay/buffer-tank-overlay.component';
import { HybridProPanelComponent } from './installation/hybrid-pro-panel/hybrid-pro-panel.component';
import { HybridProGeneratorModesPipe } from './installation/hybrid-pro-panel/pipes/hybrid-pro-generator-modes.pipe';
import { HybridProOperatingModesActivePipe } from './installation/hybrid-pro-panel/pipes/hybrid-pro-operating-modes-active.pipe';
import { HybridProOverlayComponent } from './installation/hybrid-pro-panel/hybrid-pro-overlay/hybrid-pro-overlay.component';
import { ConstraintsPipe } from '../operational-data-common/feature-pipes/constraints.pipe';
import { GasConfigurationTypePipe } from './installation/system/system-overlay/device-overlay/pipes/gas-configuration-type.pipe';
import { AlternateClassStylingDirective } from '@vi-iot/ui/directives/alternate-styling/alternate-class-styling.directive';
import { InternalHydraulicTranslationsPipe } from './installation/internal-hydraulic-features-panel/internal-hydraulic-translations.pipe';

@NgModule({
  imports: [
    DiagnosticsModule,
    DiagnosticsOverlayModule,
    SingleRoomControlModule,
    HighchartsChartModule,
    SharedDependenciesModule,
    SharedUiModule,
    FeaturesModule,
    InfiniteScrollModule,
    InstallationsRouting,
    GoogleMapsModule,
    DragScrollComponent,
    DragScrollItemDirective,
    NgxMaskModule,
    OwnerCardComponent,
    PhotovoltaicsOverlayComponent,
    ViDswFooterComponent,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatSelectModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    OptimizationBannerComponent,
    OptimizationOverlayComponent,
    DiverterValvePipe,
    TranslateGatewayConnectionModePipe,
    GenericInfoBannerComponent,
    MatTooltipModule,
    PowerLimitationStatusesComponent,
    AccessListUserComponent,
    HybridProGeneratorModesPipe,
    HybridProOperatingModesActivePipe,
    AlternateClassStylingDirective,
  ],
  declarations: [
    InstallationsComponent,
    InstallationFilterComponent,
    InstallationDashboardComponent,
    InstallationListComponent,
    InstallationItemComponent,
    InstallationItemStatusComponent,
    LimitedSearchComponent,

    // Installation details
    InstallationLimitedComponent,
    InstallationComponent,
    SystemComponent,
    TemperatureScheduleManagerComponent,
    EventListComponent,
    EventItemComponent,
    CustomerIdentifierComponent,
    OperatingModeIndicatorComponent,
    OperatingModeIndicatorsComponent,
    HolidayModePanelComponent,
    HeatCurvePanelComponent,
    CircuitOperatingModePanelComponent,
    DomesticHotWaterPanelComponent,
    HeatingPanelComponent,
    SetHolidayModeComponent,
    VentilationModePanelComponent,
    VentilationPanelComponent,
    HeatingCircuitIgnoredModalComponent,
    MissedHcAssignmentWarningModalComponent,
    DeregisterConfirmationModalComponent,
    NoteModalComponent,
    DeregisterConfirmationModalComponent,
    FlowLimitTempConfirmationDialogComponent,
    MapComponent,
    RefrigerantCircuitHistoryModalComponent,
    LagDeviceDetailsOverlayComponent,
    LagDeviceTileComponent,
    RefrigerantCircuitHistoryDetailsComponent,
    RefrigerantCircuitHistoryItemComponent,
    ConsumptionHistoryComponent,
    EnergyFlowComponent,
    TrendingComponent,
    TrendingDateRangeComponent,
    TransferInstallationDialogComponent,
    EnergyHistoryChartLegendComponent,
    EnergyFiltersHeaderComponent,
    DayDatepickerComponent,
    YearDatepickerComponent,
    EnergyHistoryChartComponent,
    CircuitHeatingZoneFeaturesPanelComponent,
    CircuitHeatingZoneDetailsOverlayComponent,
    CallForHeatHeatingZoneFeaturesPanelComponent,
    CallForHeatHeatingZoneDetailsOverlayComponent,
    HeatingZoneHeatCurvePanelComponent,
    StickyPanelComponent,
    OwnerNamePanelComponent,
    InstallationDetailsMenuBarComponent,
    NoteCardComponent,
    ActiveErrorsCardComponent,
    BuildingManagerCardComponent,
    ActiveErrorComponent,
    LockedSystemErrorComponent,
    EcoModePanelComponent,
    ComfortModePanelComponent,
    FlowLimitTemperaturePanelComponent,
    SingleFamilyHousePanelComponent,
    MultiFamilyHousePanelComponent,
    MfhTemperaturePanelComponent,
    MfhProgramsPanelComponent,
    InstallationOrderedElementComponent,
    InstallationDetailsHeaderComponent,
    OverlayComponent,
    WarmWaterComponent,
    WarmWaterOverlayComponent,
    GatewayFeaturesPanelComponent,
    GatewayDetailsOverlayComponent,
    FuelCellComponent,
    FuelCellDetailsOverlayComponent,
    HeatingCircuitFeaturesPanelComponent,
    HeatingCircuitDetailsOverlayComponent,
    TemperatureScheduleManagerComponent,
    DragDropMobileComponent,
    SystemOverlayComponent,
    ControlUnitsComponent,
    MaintenanceChecklistComponent,
    VentilationOverlayComponent,
    VentilationBypassComponent,
    VitotrolRemoteControllerComponent,
    SelectVitotrolDeviceModalComponent,
    UnassignVitotrolRemoteControllerConfirmModalComponent,
    VentilationQuickSelectionsComponent,
    VentilationComponent,
    RefrigerantCircuitFeaturesPanelComponent,
    RefrigerantCircuitDetailsOverlayComponent,
    DefrostingRemoteTriggerComponent,
    DefrostingProcessRunningComponent,
    EventLogComponent,
    LectureCodeAddressPanelComponent,
    RemoteServiceTabsComponent,
    RemoteServiceThresholdsComponent,
    RemoteServiceThresholdsTileComponent,
    RemoteServiceChartComponent,
    RemoteServiceCardComponent,
    InvitationListComponent,
    AccessListComponent,
    AccessListTableComponent,
    SystemDetailsComponent,
    EcotronicDetailsComponent,
    HemsComponent,
    ChartParametersPopupComponent,
    ElectricHeaterComponent,
    ElectricHeaterOverlayComponent,
    SolarComponent,
    SolarOverlayComponent,
    PvHeaterComponent,
    PvHeaterOverlayComponent,
    HolidayDatepickerModalComponent,
    HolidayModeDatepickerComponent,
    SetHolidayModeComponent,
    HolidayCardComponent,
    HolidayIconComponent,
    HolidayModeSelectComponent,
    BatteryComponent,
    NbiotComponent,
    ViCarePlansComponent,
    PhotovoltaicsComponent,
    BatteryOverlayComponent,
    GenericConsumptionChartPanelComponent,
    EnergyCockpitPanelComponent,
    GenericConsumptionChartOverlayComponent,
    DepartmentsDropdownComponent,
    HeatingTemperatureComponent,
    CoolingTemperatureComponent,
    CalculationStatusPipe,
    BookedPackageCardComponent,
    RoleManagementModalComponent,
    SequencingStrategyComponent,
    SequencingStrategyTileComponent,
    MainDeviceTileComponent,
    MainDevicesListSmallComponent,
    MainDevicesListLargeComponent,
    HemsSettingsComponent,
    EditHeatingCircuitNameModalComponent,
    ProgramTogglePanelComponent,
    HemsActivateDeactivateSettingOptionComponent,
    HemsStepperSettingOptionComponent,
    HemsFinancialOptimizationComponent,
    DeviceOverlayComponent,
    DefaultFeatureTilesComponent,
    V2CascadeFeatureTilesComponent,
    V2DeviceFeatureTilesComponent,
    EcoWaterOverlayComponent,
    RefrigerantLoadClassesDetailsComponent,
    AnnouncementBannerComponent,
    EcoWaterComponent,
    BufferTankComponent,
    FirmwareUpdateModalComponent,
    FirmwareUpdateStartComponent,
    FirmwareUpdateProgressComponent,
    FirmwareUpdateUpToDateComponent,
    FirmwareUpdateVerificationComponent,
    FirmwareUpdateVersionListComponent,
    FirmwareUpdateOneTimeApprovalComponent,
    FirmwareUpdateOperationStatusComponent,
    FirmwareUpdateVersionListCheckComponent,
    FirmwareUpdateVersionListUpdatingComponent,
    FirmwareUpdateVersionListFinalizeComponent,
    RemoteControllerComponent,
    RemoteControllerOverlayComponent,
    DeviceTileWrapperComponent,
    CommonInstallationTileComponent,
    WallboxSystemComponent,
    WallboxChargingModeComponent,
    WallboxChargingModeOverlayComponent,
    CascadeDevicesBadgePipe,
    VentilationDiagnosticInformationComponent,
    RemoteServiceFeaturesComponent,
    RemoteServiceReportComponent,
    RemoteServiceDiagnosisComponent,
    RemoteServiceOverlayComponent,
    EditOwnerCardDialogComponent,
    WallboxSystemOverlayComponent,
    ViCarePlansOverlayComponent,
    ServiceInstructionComponent,
    TrendingXrangeChartComponent,
    TrendingLineChartComponent,
    SolarLogComponent,
    SolarLogOverlayComponent,
    VicareThermostatComponent,
    VicareThermostatOverlayComponent,
    SystemV2CascadeComponent,
    MaintenanceInformationOverlayComponent,
    RepeatersWifiLqiComponent,
    MaintenanceNotificationComponent,
    ControlUnitsOverlayComponent,
    SmartHomeComponentsComponent,
    PartnerAccessOverlayComponent,
    PartnerAccessSettingsOverlayComponent,
    PartnerAccessSettingsItemComponent,
    PartnerAccessShareDeviceModalComponent,
    PartnerAccessPillsPipe,
    GatewayFirmwareUpdateButtonComponent,
    PartnerAccessSharePillPipePipe,
    WallboxFeatureTilesComponent,
    InstallationCardComponent,
    GenericConsumptionPanelComponent,
    GenericConsumptionOverlayComponent,
    RecirculationSettingsComponent,
    HygieneComponent,
    GatewayMenuComponent,
    VentilationSensorReadingsComponent,
    BatteryOverallComponent,
    PhotovoltaicOverallComponent,
    TrendingChartLegendComponent,
    VentilationAutomatedModeLevelComponent,
    VentilationResetToDefaultConfirmModalComponent,
    InternalHydraulicFeaturesPanelComponent,
    InternalHydraulicDetailsOverlayComponent,
    InternalHydraulicRelatedParametersComponent,
    InternalHydraulicVolumeFlowExplanationComponent,
    InternalHydraulicPermanentInspectionTeaserComponent,
    FirmwareUpdateUpToDateComponent,
    FirmwareUpdateProgressComponent,
    FanRingCondensatePanHeaterComponent,
    SystemConfigurationsComponent,
    VentilationFrostProtectionComponent,
    VentilationRemoteControllerReadingsComponent,
    SystemServiceIntervalComponent,
    ReplaceGatewayModalComponent,
    FloorHeatingOverHeatingErrorComponent,
    ServiceLayerOverlayComponent,
    FirmwareUpdateVersionListBannerComponent,
    PowerLimitationHistoryOverlayComponent,
    PowerLimitationHistoryFiltersComponent,
    PowerLimitationHistoryListComponent,
    BufferTankOverlayComponent,
    HybridProPanelComponent,
    HybridProOverlayComponent,
    GasConfigurationTypePipe,
    InternalHydraulicTranslationsPipe,
  ],
  providers: [
    CustomDateAdapter,
    { provide: DateAdapter, useClass: CustomDateAdapter },
    ExecutablePipe,
    InputValuePipe,
    UnitPipe,
    FeatureLikePipe,
    Value2Pipe,
    HybridProOperatingModesActivePipe,
    ConstraintsPipe,
    GasConfigurationTypePipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class InstallationsModule {
  constructor(router: Router, viewportScroller: ViewportScroller) {
    const navigationStart$: Observable<NavigationStart> = router.events.pipe(
      filter((e: Event): e is NavigationStart => e instanceof NavigationStart)
    );

    const navigationEnd$: Observable<NavigationEnd> = router.events.pipe(
      filter((e: Event): e is NavigationEnd => e instanceof NavigationEnd)
    );

    const navigationScroll$: Observable<Scroll> = router.events.pipe(
      filter((e: Event): e is Scroll => e instanceof Scroll)
    );

    // Prevent scrolling in installation list after query params change
    navigationStart$
      .pipe(
        withLatestFrom(navigationEnd$),
        filter(([navigationStart, navigationEnd]) =>
          this.validateInstallationsListScrollConditions(navigationStart, navigationEnd)
        ),
        // Offset has to be checked before Scroll event
        switchMap(() => navigationScroll$)
      )
      .subscribe((scrollEvent) => {
        if (scrollEvent.position) {
          // backward navigation
          viewportScroller.scrollToPosition(scrollEvent.position);
        } else if (scrollEvent.anchor) {
          // anchor navigation
          viewportScroller.scrollToAnchor(scrollEvent.anchor);
        } else {
          // forward navigation
        }
      });
  }

  private validateInstallationsListScrollConditions(
    navigationStart: NavigationStart,
    navigationEnd: NavigationEnd
  ): boolean {
    const navigationStartRouteSplit = navigationStart.url.split('?');
    const navigationStartRoute = navigationStartRouteSplit[0];
    const navigationStartPageValue = this.extractQueryParamValue(
      navigationStartRouteSplit[1],
      'page'
    );

    const navigationEndRouteSplit = navigationEnd.url.split('?');
    const navigationEndRoute = navigationEndRouteSplit[0];
    const navigationEndPageValue = this.extractQueryParamValue(navigationEndRouteSplit[1], 'page');

    const isOnSamePaginatorPage = navigationStartPageValue === navigationEndPageValue;

    return (
      navigationStartRoute === navigationEndRoute &&
      navigationEndRoute === '/installations' &&
      isOnSamePaginatorPage
    );
  }

  private extractQueryParamValue(queryParams: string, queryParamName: string): string {
    if (!queryParams) {
      return;
    }

    const queryParamsToArray = queryParams.split('&');
    const specificParam = queryParamsToArray.find((param) => param.startsWith(queryParamName));

    return specificParam;
  }
}
